import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { Control } from "react-hook-form";
import { z } from "zod";
import { formSchema } from "../schema";

interface AdditionalSettingsFormProps {
  control: Control<z.infer<typeof formSchema>>;
}

export const AdditionalSettingsForm = ({
  control,
}: AdditionalSettingsFormProps) => {
  return (
    <Card className="shadow-md mt-6">
      <CardHeader>
        <CardTitle>Additional Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={control}
          name="allowsMultipleAssignments"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Allows Multiple Assignments</FormLabel>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="allowsOverlappingEvents"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Allows Overlapping Events</FormLabel>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isTable"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <div className="flex items-center space-x-2">
                  <FormLabel>Table Game</FormLabel>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    {/* What it does, not just when to tick it. The old copy
                        said only which positions qualify, so the one thing
                        that actually changes — two lines on a document a
                        client reads — was left to be discovered. */}
                    <TooltipContent>
                      <div className="max-w-xs space-y-2">
                        <p>
                          Check this box for a table a guest sits at (Blackjack,
                          Roulette, Poker) rather than a service like setup,
                          delivery or photography.
                        </p>
                        <p>
                          It controls the <strong>Total Tables</strong> and{" "}
                          <strong>Guests Accommodated</strong> lines on the
                          printed bid: only checked positions are counted, so a
                          driver or a manager is billed as a line item without
                          being counted as a table. Guest numbers come from
                          Guests Accommodated on this position.
                        </p>
                        <p>
                          It does not affect what the client is billed or what
                          the dealer is paid.
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="requestable"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <div className="flex items-center space-x-2">
                  <FormLabel>Requestable</FormLabel>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Check this box if users are able to request to be passed
                        off in this position
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isManager"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <div className="flex items-center space-x-2">
                  <FormLabel>Manager Position</FormLabel>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Indicates that this position has authority over this
                        event. Determines if the "Pitboss emails" are sent to
                        this position.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isFlatRate"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <div className="flex items-center space-x-2">
                  <FormLabel>Flat Rate Position</FormLabel>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Indicates that this position is paid a flat rate rather
                        than hourly. ONLY AFFECTS EMPLOYEE PAY. DOES NOT AFFECT
                        HOW MUCH THE CLIENT IS BILLED FOR THIS POSITION.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};
